// HighwaysIndustry.com, Content / Media / Branding business
// Practical, commercial, industry-focused. No consultancy jargon.

// 15 positions a client can be known for (Section 4 / Position pillar)
export const POSITIONS = [
  { value: "safety", label: "Safety" },
  { value: "innovation", label: "Innovation" },
  { value: "technology", label: "Technology" },
  { value: "sustainability", label: "Sustainability" },
  { value: "social-value", label: "Social Value" },
  { value: "leadership", label: "Leadership" },
  { value: "expertise", label: "Expertise" },
  { value: "quality", label: "Quality" },
  { value: "reliability", label: "Reliability" },
  { value: "customer-service", label: "Customer Service" },
  { value: "collaboration", label: "Collaboration" },
  { value: "people-and-culture", label: "People & Culture" },
  { value: "operational-excellence", label: "Operational Excellence" },
  { value: "carbon-reduction", label: "Carbon Reduction" },
  { value: "project-delivery", label: "Project Delivery" },
];

// The Three Pillars: Create / Amplify / Position
export const THREE_PILLARS = [
  {
    code: "01",
    key: "create",
    label: "Create",
    headline: "Everything starts with having something worth showing.",
    intro:
      "Professional content built specifically for the highways and infrastructure sector.",
    items: [
      "Video",
      "Photography",
      "Drone Content",
      "Articles",
      "Case Studies",
      "Social Media Assets",
      "Graphic Design",
      "Websites",
    ],
  },
  {
    code: "02",
    key: "amplify",
    label: "Amplify",
    headline: "Creating content is only half the job.",
    intro:
      "People need to see it. We push your content into the channels your audience already reads.",
    items: [
      "HighwaysIndustry.com",
      "Newsletter",
      "LinkedIn",
      "Facebook",
      "Email Marketing",
      "Social Media",
      "Industry Audience",
      "Industry Networks",
    ],
  },
  {
    code: "03",
    key: "position",
    label: "Position",
    headline: "Consistent visibility creates recognition.",
    intro:
      "Recognition creates trust. Trust creates opportunity. Pick what you want to be known for.",
    items: POSITIONS.map((p) => p.label),
  },
];

// Services, each becomes a /services/{slug} page
// Videos are 540p H.264 web-optimised loops served from /public/videos/.
// To swap: drop your MP4 at /app/frontend/public/videos/{slug}.mp4 and the
// path below will pick it up automatically.
const HW_VIDEOS = {
  droneFilming: "/videos/drone-filming.mp4",
  groundFilming: "/videos/ground-filming.mp4",
  editing: "/videos/editing.mp4",
  cgi: "/videos/cgi.mp4",
  photography: "/videos/photography.mp4",
  contentWriting: "/videos/content-writing.mp4",
  socialMedia: "/videos/social-media.mp4",
  websiteDesign: "/videos/website-design.mp4",
  graphicDesign: "/videos/graphic-design.mp4",
  industryReach: "/videos/industry-reach.mp4",
  audienceGrowth: "/videos/audience-growth.mp4",
  brandDevelopment: "/videos/brand-development.mp4",
};

export const SERVICES = [
  {
    slug: "drone-filming",
    label: "Drone Filming",
    short:
      "Aerial cinematography of sites, assets and live infrastructure projects.",
    overview:
      "Licensed drone pilots capturing your assets, sites and progress from above. Ideal for project showcases, framework submissions and time-lapse coverage of major works.",
    deliverables: [
      "Site overview aerials",
      "Asset and project showcases",
      "Progress / time-lapse coverage",
      "Tracking and reveal shots",
      "Cinematic transitions for video edits",
      "Aerial photography stills for bids and press",
    ],
    video: HW_VIDEOS.droneFilming,
    image:
      "https://images.unsplash.com/photo-1473968512647-3e447244af8f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    whyItMatters: {
      headline: "Turn completed projects into future opportunities.",
      body: [
        "Highways schemes take months to deliver, involve huge investment and showcase the capability of your team. But once the job is complete, the opportunity to capture that work is gone.",
        "Professional drone filming helps you document progress, promote success, support bids, engage stakeholders and build a stronger brand presence across the industry.",
      ],
    },
    // Two-image gallery, shown on the Service Detail page between Overview and Deliverables
    gallery: [
      {
        src: "/images/services/drone-filming/drone-hs2-viaduct.jpg",
        alt: "DJI drone shot of an HS2 arched viaduct under construction",
        badge: "AERIAL · HS2 VIADUCT",
      },
      {
        src: "/images/services/drone-filming/drone-motorway.jpg",
        alt: "DJI Inspire drone flying over a live UK motorway",
        badge: "TRACKING · CARRIAGEWAY",
      },
    ],
  },
  {
    slug: "ground-filming",
    label: "Ground Filming",
    short:
      "On-site filming, interviews, recruitment content and project storytelling.",
    overview:
      "Full ground-based video shoots on live sites and in the office. Multi-camera setups, lavalier audio, lighting, everything required to capture credible, broadcast-quality footage in the conditions our sector actually works in.",
    deliverables: [
      "On-site interviews with engineers and directors",
      "Operative-led story films",
      "Recruitment / EVP shoots",
      "Project documentary coverage",
      "Event filming and roundtables",
      "Multi-camera, lit and lav-mic'd set-ups",
    ],
    video: HW_VIDEOS.groundFilming,
    image:
      "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    whyItMatters: {
      headline: "Tell the story behind the work.",
      body: [
        "Every project has people, challenges and achievements that often go unseen. The teams delivering the work are what make your business different.",
        "Professional ground filming captures the knowledge, expertise and personalities behind your company, helping you build trust, attract talent, promote success and show the industry what happens beyond the finished scheme.",
      ],
    },
    gallery: [
      {
        src: "/images/services/ground-filming/chevron-tablet-briefing.jpg",
        alt: "Chevron Traffic Management team reviewing a live-site briefing on a tablet",
        badge: "BRIEFING · SITE TEAM",
      },
      {
        src: "/images/services/ground-filming/cameraman-tripod.jpg",
        alt: "Cinematographer on a tripod capturing behind-the-scenes footage on site",
        badge: "BEHIND · THE SCENES",
      },
    ],
  },
  {
    slug: "photography",
    label: "Photography",
    short:
      "Site photography, team portraits, project imagery and event coverage.",
    overview:
      "Imagery designed for bids, websites and social. Shot by photographers who understand sites, PPE and what looks credible to clients.",
    deliverables: [
      "Site and project photography",
      "Team and director portraits",
      "Event coverage",
      "Bid library imagery",
      "Headshots and lifestyle",
      "Product and equipment stills",
    ],
    video: HW_VIDEOS.photography,
    image:
      "https://images.unsplash.com/photo-1452587925148-ce544e77e70d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    whyItMatters: {
      headline: "Build a professional image around your business.",
      body: [
        "The quality of your imagery shapes how people see your company. Great projects, people and equipment deserve more than basic site photos.",
        "Professional photography creates a library of high-quality assets you can use across bids, websites, social media, awards and marketing, keeping your business looking consistent and credible.",
      ],
    },
    gallery: [
      {
        src: "/images/services/photography/king-emcc-van.jpg",
        alt: "KING EMCC National Highways emergency vehicle moving on a live UK carriageway",
        badge: "FLEET · KING EMCC",
      },
      {
        src: "/images/services/photography/tunnel-camera-operator.jpg",
        alt: "Skyways camera operator filming inside a tunnel under construction",
        badge: "TUNNEL · CAMERA OPERATOR",
      },
    ],
  },
  {
    slug: "editing",
    label: "Editing",
    short:
      "Edit, colour, sound and finishing for project films and social content.",
    overview:
      "We turn raw footage into broadcast-quality films. Edit, motion graphics, colour grading, sound design and finishing, for the case studies, recruitment films and social content that put your work on the feed.",
    deliverables: [
      "Long-form project edits",
      "Short-form social cuts",
      "Motion graphics and titles",
      "Colour grading and sound design",
      "Subtitling and accessibility",
      "Format-ready exports (LinkedIn, YouTube, Instagram, 4K)",
    ],
    video: HW_VIDEOS.editing,
    image:
      "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    whyItMatters: {
      headline: "Turn raw content into something people remember.",
      body: [
        "Capturing footage is only the beginning. The edit is where your story, message and results come together.",
        "Professional editing turns site footage, interviews and project content into polished videos designed for social media, presentations, bids and campaigns that represent your business properly.",
      ],
    },
    gallery: [
      {
        src: "/images/services/editing/night-tm-works.webp",
        alt: "Night-time traffic management operative kneeling beside a cone with light trails from passing traffic",
        badge: "NIGHT · TRAFFIC MANAGEMENT",
      },
      {
        src: "/images/services/editing/heavy-plant-earthworks.webp",
        alt: "Plantforce articulated dump truck kicking up dust in convoy with earthworks plant",
        badge: "EARTHWORKS · IN MOTION",
      },
    ],
  },
  {
    slug: "cgi",
    label: "CGI",
    short:
      "3D visualisation, render and animation for proposals, bids and marketing.",
    overview:
      "Photo-real CGI for assets, schemes and concepts that haven't been built yet, or can't be safely filmed. Perfect for bid submissions, planning consultations and showcasing future projects.",
    deliverables: [
      "Asset and scheme visualisations",
      "Animated walk-throughs and fly-overs",
      "Bid render packs",
      "Photo-real product / equipment renders",
      "Live data overlays and HUD graphics",
      "Concept-to-live comparison sequences",
    ],
    video: HW_VIDEOS.cgi,
    image:
      "https://images.unsplash.com/photo-1633477189729-9290b3261d0a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    whyItMatters: {
      headline: "Show what cannot yet be seen.",
      body: [
        "Some projects need explaining before they exist. Complex schemes, technology and ideas are often difficult to communicate with words alone.",
        "CGI helps bring concepts to life, making proposals, bids and presentations clearer, more engaging and easier for clients and stakeholders to understand.",
      ],
    },
    gallery: [
      {
        src: "/images/services/cgi/cgi-studio-visualisation.jpg",
        alt: "Split-frame CGI comparison showing live roadworks scene above and wireframe overpass model below",
        badge: "DESIGN · BUILD · MAINTAIN",
      },
      {
        src: "/images/services/cgi/cgi-works-split.jpg",
        alt: "3D artist working on a highway visualisation inside a studio wall marked 'Visualising Infrastructure Solutions'",
        badge: "STUDIO · VISUALISATION",
      },
    ],
  },
  {
    slug: "content-writing",
    label: "Content Writing",
    short:
      "Articles, case studies, press releases and editorial-grade features.",
    overview:
      "Words written by people who know the sector. We turn your projects, people and data into content your clients actually want to read.",
    deliverables: [
      "Long-form features",
      "Case study write-ups",
      "Press releases",
      "Website copy",
      "LinkedIn ghostwriting for senior leaders",
      "Newsletter content",
    ],
    video: HW_VIDEOS.contentWriting,
    image:
      "https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    whyItMatters: {
      headline: "Make sure your achievements get noticed.",
      body: [
        "Great work happens across the highways industry every day, but without the right story it can easily be missed.",
        "Professional content writing turns projects, innovations and company news into powerful articles, case studies and features that build reputation and keep your business visible.",
      ],
    },
    gallery: [
      {
        src: "/images/services/content-writing/partner-content-desk.jpg",
        alt: "Editor's desk with a HighwaysIndustry.com branded mug, keyboard and monitor showing the Partner Content page live",
        badge: "PARTNER CONTENT · LIVE",
      },
      {
        src: "/images/services/content-writing/partner-content-monitor.jpg",
        alt: "Close-up of a monitor displaying HighwaysIndustry.com Partner Content articles and features",
        badge: "FEATURE GRID · IN PUBLICATION",
      },
    ],
  },
  {
    slug: "social-media",
    label: "Social Media Management",
    short:
      "Content creation, management and posting across LinkedIn, Facebook and more.",
    overview:
      "We run your channels like an editor, not a scheduler. Consistent, on-brand, and built around content people actually want to share.",
    deliverables: [
      "Channel strategy and calendar",
      "Daily/weekly posting",
      "LinkedIn for directors and SMEs",
      "Community management",
      "Performance reporting",
      "Sector-tailored content creation",
    ],
    video: HW_VIDEOS.socialMedia,
    image:
      "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    whyItMatters: {
      headline: "Stay visible with the people who matter.",
      body: [
        "Your customers, partners and future employees are already watching the industry online. Consistent content keeps your company part of the conversation.",
        "Professional social media management helps showcase your work, grow your audience and maintain a strong presence without taking time away from your team.",
      ],
    },
    gallery: [
      {
        src: "/images/services/social-media/hootsuite-dashboard.jpg",
        alt: "Social media content planner open on a laptop while a phone shows the finished Instagram grid",
        badge: "PLAN · CREATE · SCHEDULE",
      },
      {
        src: "/images/services/social-media/content-planner-mobile.jpg",
        alt: "Hootsuite dashboard tracking eight connected HighwaysIndustry.com accounts across Facebook, X, Instagram, LinkedIn, YouTube, TikTok, Threads and Bluesky",
        badge: "MULTI-CHANNEL · MANAGEMENT",
      },
    ],
  },
  {
    slug: "website-design",
    label: "Website Design",
    short:
      "Modern websites built to showcase your business and convert visitors.",
    overview:
      "We design and build websites that look as good as the work you do. Fast, modern, mobile-first, and built so your team can update them without a developer.",
    deliverables: [
      "UX and site architecture",
      "Visual design",
      "Build and launch",
      "Mobile-first responsive",
      "Search-engine setup",
      "Easy editing for your team",
    ],
    video: HW_VIDEOS.websiteDesign,
    image:
      "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    whyItMatters: {
      headline: "Make your online presence match your capability.",
      body: [
        "Your website is often the first place people go to understand your business. It should reflect the quality of the work you deliver.",
        "A modern website helps showcase projects, explain services, attract enquiries and create confidence before someone speaks to your team.",
      ],
    },
    gallery: [
      {
        src: "/images/services/website-design/web-drone-services-imac.jpg",
        alt: "Highways Drone Services website homepage displayed on a large desktop screen with the strapline 'Fast clear drone data for your projects'",
        badge: "DESKTOP · HERO BUILD",
      },
      {
        src: "/images/services/website-design/web-htm-responsive.jpg",
        alt: "Highway Traffic Management (HTM) Streets Ahead website shown across laptop, tablet and mobile in a responsive layout",
        badge: "RESPONSIVE · MULTI-DEVICE",
      },
    ],
  },
  {
    slug: "graphic-design",
    label: "Graphic Design",
    short:
      "Brochures, social graphics, presentations and brand collateral.",
    overview:
      "Design that makes your business look the size it deserves to be. Whether it's a bid document, a tender pack or your LinkedIn feed.",
    deliverables: [
      "Bid documents and brochures",
      "Social graphics",
      "Presentation decks",
      "Brand guidelines",
      "Infographics and data visuals",
      "Award submission design",
    ],
    video: HW_VIDEOS.graphicDesign,
    image:
      "https://images.unsplash.com/photo-1626785774573-4b799315345d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    whyItMatters: {
      headline: "Make every piece of communication represent your brand.",
      body: [
        "From presentations to brochures, the way your information looks affects how your company is remembered.",
        "Professional design helps turn ideas, projects and messages into clear, consistent materials that support sales, bids, recruitment and marketing.",
      ],
    },
    gallery: [
      {
        src: "/images/services/graphic-design/design-drafting.jpg",
        alt: "Designer sketching layouts at a wooden desk with a scale ruler and rolled artwork",
        badge: "CONCEPT · IN THE STUDIO",
      },
      {
        src: "/images/services/graphic-design/design-moodboard.jpg",
        alt: "Laptop on a warm-lit desk displaying a moodboard of layouts alongside a copper mug",
        badge: "LAYOUT · MOODBOARD",
      },
    ],
  },
  {
    slug: "industry-reach",
    label: "Industry Reach",
    short:
      "Direct access to one of the largest dedicated highways audiences in the UK.",
    overview:
      "This is the part no marketing agency can replicate. Your content goes out through HighwaysIndustry.com, our newsletter, our social channels and our industry network.",
    deliverables: [
      "Feature placement on HighwaysIndustry.com",
      "Newsletter inclusion",
      "Social distribution",
      "Sector network amplification",
      "Email marketing to named audiences",
      "Podcast, webinar and event exposure",
    ],
    video: HW_VIDEOS.industryReach,
    image:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    whyItMatters: {
      headline: "Put your business in front of the highways sector.",
      body: [
        "Creating great content is only valuable if the right people see it. Visibility builds awareness, trust and opportunity.",
        "Through established industry platforms and audiences, your news, projects and achievements reach the people who matter across highways and infrastructure.",
      ],
    },
    gallery: [
      {
        src: "/images/services/industry-reach/reach-boardroom.jpg",
        alt: "Sunlit boardroom of decision-makers reviewing content on laptops around a long conference table",
        badge: "DECISION MAKERS · IN THE ROOM",
      },
      {
        src: "/images/services/industry-reach/reach-site-survey.jpg",
        alt: "Aerial view of a construction team in high-vis reviewing works across a live rebar deck",
        badge: "AUDIENCE · ACROSS THE NETWORK",
      },
    ],
  },
  {
    slug: "audience-growth",
    label: "Audience Growth",
    short:
      "Helping more of the right people discover and follow your business.",
    overview:
      "Followers don't matter. The right followers do. We grow audiences that buy from you, work for you or refer you.",
    deliverables: [
      "LinkedIn audience growth",
      "Newsletter list building",
      "Targeted reach campaigns",
      "Inbound content strategy",
      "Conversion-focused content",
      "Analytics and reporting dashboards",
    ],
    video: HW_VIDEOS.audienceGrowth,
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    whyItMatters: {
      headline: "Build an audience before you need one.",
      body: [
        "Companies with strong audiences have more opportunities to share success, attract talent and stay recognised.",
        "Audience growth helps increase your visibility with the right people, creating a stronger long-term presence across the highways sector.",
      ],
    },
    gallery: [
      {
        src: "/images/services/audience-growth/growth-workshop.jpg",
        alt: "Team mapping a content strategy on post-it notes across a studio wall",
        badge: "STRATEGY · SPRINT",
      },
      {
        src: "/images/services/audience-growth/growth-strategy.jpg",
        alt: "Analyst reviewing performance dashboards on a laptop while briefing a colleague",
        badge: "GROWTH · MEASURED",
      },
    ],
  },
  {
    slug: "brand-development",
    label: "Brand Development",
    short:
      "Visual identity, tone of voice and brand guidelines built for highways.",
    overview:
      "Your brand should look as serious as the work you do. We build identities that hold up in tender packs, on hard hats and on LinkedIn feeds.",
    deliverables: [
      "Logo and visual identity",
      "Tone of voice",
      "Brand guidelines",
      "Stationery and bid templates",
      "Roll-out across digital and print",
      "Vehicle livery and site branding",
    ],
    video: HW_VIDEOS.brandDevelopment,
    image:
      "https://images.unsplash.com/photo-1572177812156-58036aae439c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    whyItMatters: {
      headline: "Create a brand people recognise and remember.",
      body: [
        "A strong brand is more than a logo. It is how your company looks, sounds and is understood across the industry.",
        "Brand development helps create a clear identity, consistent message and stronger position that reflects the quality of your business.",
      ],
    },
    gallery: [
      {
        src: "/images/services/brand-development/brand-studio.jpg",
        alt: "Bright loft-style studio with mid-century seating, a sculptural black floor lamp and warm timber joinery",
        badge: "IDENTITY · IN THE STUDIO",
      },
      {
        src: "/images/services/brand-development/brand-workshop.jpg",
        alt: "Brand team reviewing identity work together around a table lit by pendant lighting",
        badge: "WORKSHOP · BRAND SPRINT",
      },
    ],
  },
];

// Helpful service helper
export const getService = (slug) => SERVICES.find((s) => s.slug === slug);

// Short pun-adjacent tagline per service, rendered as a cyan eyebrow on
// tiles across the home page and What We Do grid.
export const SERVICE_TAGLINES = {
  "drone-filming": "Aerial that lands.",
  "ground-filming": "Cameras that roll.",
  photography: "Frames that stick.",
  editing: "Cuts that carry.",
  cgi: "Renders that ring true.",
  "content-writing": "Writing that gets read.",
  "social-media": "Scrolls that stop.",
  "website-design": "Sites that click.",
  "graphic-design": "Design that pops.",
  "industry-reach": "Reach that travels.",
  "audience-growth": "Audiences that compound.",
  "brand-development": "Brands that stick.",
};

// Case studies, simplified, industry-focused, no jargon
export const CASE_STUDIES = [
  {
    slug: "sustainability-visibility",
    position: "sustainability",
    client: "A national highways contractor",
    title: "From invisible to recognised on sustainability",
    summary:
      "A Tier 2 contractor doing strong low-carbon work, but no-one outside their client list had heard of it. 12 months later, they were on every shortlist for green frameworks.",
    challenge:
      "Genuinely good sustainability work that wasn't being seen. The team was too busy delivering to publish anything. Procurement teams didn't recall them on net-zero briefs.",
    approach:
      "We built a 12-month content programme, monthly project films, weekly long-form articles, a dedicated low-carbon LinkedIn presence, and feature placement on HighwaysIndustry.com.",
    result:
      "3 framework wins, named in two Tier 1 ESG submissions, recognised by the sector as a default name on sustainability briefs.",
    metrics: [
      { label: "Framework wins", value: "3" },
      { label: "Named in Tier 1 bids", value: "2" },
      { label: "LinkedIn audience growth", value: "+312%" },
    ],
    image:
      "https://images.unsplash.com/photo-1536895058696-a69b1c7ba34f?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    slug: "smart-motorways-tech",
    position: "technology",
    client: "A connected-infrastructure specialist",
    title: "Built into every smart-motorway shortlist in 9 months",
    summary:
      "Excellent engineering, almost no public presence. We turned them into the technology name the sector recognises.",
    challenge:
      "Advanced technology, no visibility. The brand was missing from the conversation about connected vehicles and smart enforcement.",
    approach:
      "Live-deployment video on site, a monthly CTO column, podcast features, executive social media, and full distribution through HighwaysIndustry.com.",
    result:
      "Quoted in 9 industry features, invited to two policy events, and shortlisted on every major smart-motorway brief in the period.",
    metrics: [
      { label: "Industry features", value: "9" },
      { label: "Event invitations", value: "2" },
      { label: "Shortlists", value: "100%" },
    ],
    image:
      "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    slug: "safety-visibility",
    position: "safety",
    client: "A regional civils contractor",
    title: "Safety record, finally seen",
    summary:
      "A great HSE record that was hidden in PDFs. We made safety the most visible thing about the business.",
    challenge:
      "Strong HSE numbers, no narrative around them. Safety was buried in compliance documents and never featured in client conversations.",
    approach:
      "Behavioural safety films, operative-led video, an HSE director social presence, and consistent feature placement on industry channels.",
    result:
      "Won two new framework lots, recognised regionally as a named safety leader, and recorded a 41% lift in inbound talent applications.",
    metrics: [
      { label: "Framework lots", value: "2" },
      { label: "Inbound applications", value: "+41%" },
      { label: "Named mentions", value: "+260%" },
    ],
    image:
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
];

export const MARKET_POSITIONS = [
  { value: "undervalued", label: "Undervalued", desc: "Doing strong work, but the market hasn't caught up." },
  { value: "hidden-gem", label: "Hidden Gem", desc: "Loved by current clients, invisible outside that circle." },
  { value: "growing-fast", label: "Growing Fast", desc: "Scaling, but the brand hasn't scaled with revenue." },
  { value: "market-leader", label: "Market Leader looking to defend", desc: "Already known, and need to stay there." },
];

export const ROADBLOCKS = [
  { value: "no-time-for-content", label: "No time for content", desc: "Senior team is too busy to publish." },
  { value: "lack-of-video-assets", label: "Lack of video assets", desc: "No usable site footage or executive video." },
  { value: "competitors-outshining", label: "Competitors outshining us on LinkedIn", desc: "Others are taking the feed." },
  { value: "poor-digital-presence", label: "Poor digital presence", desc: "Website and social don't reflect the work." },
];

// Reach metrics - figures correct as at July 2026.
// Sources: Google Analytics 4, Google Search Console, platform analytics.
export const REACH_STATS = [
  {
    value: "3.27M",
    label: "Google impressions",
    note: "annually",
  },
  {
    value: "900K+",
    label: "Annual pageviews",
    note: "74,000 monthly average",
  },
  {
    value: "300K+",
    label: "Social followers",
    note: "across platforms",
  },
  {
    value: "2.5M",
    label: "Emails delivered",
    note: "per year, 3× weekly",
  },
  {
    value: "95K",
    label: "New readers",
    note: "past 12 months",
  },
  {
    value: "16K",
    label: "Newsletter subscribers",
    note: "owned email audience",
  },
  {
    value: "12,800+",
    label: "Articles published",
    note: "since 2011",
  },
  {
    value: "15",
    label: "Years established",
    note: "trading since 2011",
  },
  {
    value: "4",
    label: "Owned platforms",
    note: "news, directory, jobs, construction",
  },
];

// Compact 4-stat subset for space-constrained tiles (Home, About).
export const REACH_STATS_HEADLINE = REACH_STATS.slice(0, 4);


// How it works, 4 step process
export const HOW_IT_WORKS = [
  {
    code: "01",
    title: "Understand Your Business",
    points: [
      "What makes you different?",
      "What do you want to be known for?",
      "Who are you trying to reach?",
    ],
  },
  {
    code: "02",
    title: "Create Content",
    points: [
      "Video.",
      "Photography.",
      "Articles.",
      "Social content.",
      "Websites.",
      "Case studies.",
    ],
  },
  {
    code: "03",
    title: "Distribute Content",
    points: [
      "Your channels.",
      "Our channels.",
      "Industry channels.",
      "Newsletter.",
      "Website.",
      "Social media.",
    ],
  },
  {
    code: "04",
    title: "Build Your Position",
    points: [
      "Consistency builds recognition.",
      "Recognition builds trust.",
      "Trust builds opportunity.",
    ],
  },
];

export const WHY_REASONS = [
  {
    title: "Industry Specialists",
    desc: "We only work in highways and infrastructure.",
  },
  {
    title: "Content Creators",
    desc: "Video, photography, writing, design and digital, all in-house.",
  },
  {
    title: "Built-In Distribution",
    desc: "One of the largest dedicated highways audiences in the UK.",
  },
  {
    title: "Everything Under One Roof",
    desc: "Create. Amplify. Position.",
  },
];

// Matt & Marc Video Hub, educational video topics
export const MATT_MARC_VIDEOS = [
  {
    slug: "what-does-highwaysindustry-do",
    title: "What Does HighwaysIndustry Actually Do?",
    desc: "A straight answer to the question people always ask first.",
    duration: "2:30",
  },
  {
    slug: "why-content-matters",
    title: "Why Content Matters",
    desc: "If your work doesn't get seen, it didn't happen, at least not commercially.",
    duration: "3:15",
  },
  {
    slug: "how-to-build-a-brand",
    title: "How To Build A Brand",
    desc: "Brand isn't a logo. It's the impression your business leaves in people's minds.",
    duration: "4:05",
  },
  {
    slug: "what-makes-a-great-case-study",
    title: "What Makes A Great Case Study?",
    desc: "The structure that turns a project into a winnable bid asset.",
    duration: "3:45",
  },
  {
    slug: "why-video-works",
    title: "Why Video Works",
    desc: "Why one good film beats ten written articles in the highways sector.",
    duration: "2:50",
  },
  {
    slug: "how-social-media-changed",
    title: "How Social Media Has Changed",
    desc: "LinkedIn isn't a CV site anymore. Here's what that means for your business.",
    duration: "3:20",
  },
  {
    slug: "how-to-become-known-for-something",
    title: "How To Become Known For Something",
    desc: "Choosing a position, and the work it takes to actually own it.",
    duration: "4:30",
  },
  {
    slug: "what-clients-get-wrong",
    title: "What Clients Get Wrong",
    desc: "The most common mistakes we see businesses make with their content.",
    duration: "3:55",
  },
  {
    slug: "how-to-measure-marketing-success",
    title: "How To Measure Marketing Success",
    desc: "Forget impressions. The metrics that actually matter to your business.",
    duration: "3:10",
  },
  {
    slug: "marketing-vs-visibility",
    title: "The Difference Between Marketing And Visibility",
    desc: "Why most companies are doing marketing but missing visibility entirely.",
    duration: "4:15",
  },
];
